import * as React from 'react';
import { useState, useRef, useCallback, useId, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Input } from '../ui/input';
import { useUserSearch, DirectoryUser } from '../../hooks/use-user-search';

/**
 * Props for the UserAutocomplete component.
 */
export interface UserAutocompleteProps {
  readonly onSelect: (user: DirectoryUser) => void;
  readonly onClear: () => void;
  readonly disabled?: boolean;
  readonly error?: string;
}

/**
 * Accessible combobox autocomplete component for searching and selecting
 * EntraID directory users. Implements WAI-ARIA combobox pattern with
 * keyboard navigation and screen reader announcements.
 *
 * Validates: Requirements 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8, 7.1, 7.3, 9.1, 9.2, 9.3, 9.4, 9.5
 */
export function UserAutocomplete({
  onSelect,
  onClear,
  disabled = false,
  error: externalError,
}: UserAutocompleteProps): React.JSX.Element {
  const { results, isLoading, error: searchError, search, clear } = useUserSearch();

  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const instanceId = useId();
  const listboxId = `${instanceId}-listbox`;

  const getOptionId = (index: number): string => `${instanceId}-option-${index}`;

  // Determine what to display in the dropdown
  const showDropdown = isOpen && inputValue.length >= 2;
  const hasResults = results.length > 0;
  const displayError = searchError;

  // Build the live region announcement
  const getLiveAnnouncement = (): string => {
    if (isLoading) {
      return 'Searching for users...';
    }
    if (displayError) {
      return displayError;
    }
    if (showDropdown && !isLoading) {
      if (hasResults) {
        return `${results.length} user${results.length === 1 ? '' : 's'} found`;
      }
      return 'No users found';
    }
    return '';
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      setHighlightedIndex(-1);

      if (value.length >= 2) {
        setIsOpen(true);
        search(value);
      } else {
        setIsOpen(false);
        clear();
        if (value.length === 0) {
          onClear();
        }
      }
    },
    [search, clear, onClear],
  );

  const selectUser = useCallback(
    (user: DirectoryUser) => {
      setInputValue(user.displayName + (user.mail ? ` (${user.mail})` : ''));
      setIsOpen(false);
      setHighlightedIndex(-1);
      clear();
      onSelect(user);
    },
    [clear, onSelect],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || !showDropdown) {
        // If dropdown is closed and user presses ArrowDown with enough text, open it
        if (e.key === 'ArrowDown' && inputValue.length >= 2) {
          setIsOpen(true);
          e.preventDefault();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          if (hasResults) {
            setHighlightedIndex((prev) => (prev + 1) % results.length);
          }
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          if (hasResults) {
            setHighlightedIndex((prev) => (prev <= 0 ? results.length - 1 : prev - 1));
          }
          break;
        }
        case 'Enter': {
          e.preventDefault();
          const selectedUser = results[highlightedIndex];
          if (highlightedIndex >= 0 && selectedUser) {
            selectUser(selectedUser);
          }
          break;
        }
        case 'Escape': {
          e.preventDefault();
          setIsOpen(false);
          setHighlightedIndex(-1);
          setInputValue('');
          clear();
          onClear();
          break;
        }
      }
    },
    [isOpen, showDropdown, hasResults, results, highlightedIndex, selectUser, clear, onClear, inputValue],
  );

  const handleOptionClick = useCallback(
    (user: DirectoryUser) => {
      selectUser(user);
    },
    [selectUser],
  );

  const handleBlur = useCallback(() => {
    // Use timeout to allow click events on options to fire first
    blurTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 150);
  }, []);

  const handleFocus = useCallback(() => {
    if (blurTimeoutRef.current !== null) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    // Re-open dropdown if there's a query
    if (inputValue.length >= 2) {
      setIsOpen(true);
    }
  }, [inputValue]);

  // Cleanup blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current !== null) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listboxRef.current) {
      const option = listboxRef.current.children[highlightedIndex] as HTMLElement | undefined;
      option?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const errorMessage = externalError || displayError;

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          highlightedIndex >= 0 && showDropdown ? getOptionId(highlightedIndex) : undefined
        }
        aria-invalid={!!errorMessage}
        aria-describedby={errorMessage ? `${instanceId}-error` : undefined}
        placeholder="Search users by name or email..."
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        disabled={disabled}
        autoComplete="off"
      />

      {/* Error message display */}
      {externalError && (
        <p id={`${instanceId}-error`} className="mt-1 text-sm text-destructive">
          {externalError}
        </p>
      )}

      {/* Dropdown listbox */}
      {showDropdown && (
        <ul
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-input bg-background shadow-md"
        >
          {isLoading && (
            <li className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground" aria-hidden="true">
              <svg
                className="h-4 w-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Searching...
            </li>
          )}

          {!isLoading && displayError && (
            <li className="px-3 py-2 text-sm text-destructive">
              {displayError}
            </li>
          )}

          {!isLoading && !displayError && !hasResults && (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              No users found
            </li>
          )}

          {!isLoading &&
            !displayError &&
            results.map((user, index) => (
              <li
                key={user.id}
                id={getOptionId(index)}
                role="option"
                aria-selected={index === highlightedIndex}
                className={cn(
                  'cursor-pointer px-3 py-2 text-sm',
                  index === highlightedIndex && 'bg-accent text-accent-foreground',
                )}
                onMouseDown={(e) => {
                  // Prevent blur from firing before click
                  e.preventDefault();
                }}
                onClick={() => handleOptionClick(user)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="font-medium">{user.displayName}</div>
                {user.mail && (
                  <div className="text-xs text-muted-foreground">{user.mail}</div>
                )}
              </li>
            ))}
        </ul>
      )}

      {/* Live region for screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {getLiveAnnouncement()}
      </div>
    </div>
  );
}
